import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Bot, Car, Clock3, Home, Mountain, Navigation, Send, Settings, Sparkles, UserRound } from "lucide-react";
import { api, type ChatMessage, type StoredChatMessage } from "../lib/api";
import type { HomeLocation, TravelChatAnswer } from "../lib/ai-chat";
import { weatherKind } from "../lib/weather";
import { WeatherIcon } from "./WeatherIcon";

const suggestions = [
  "Dimmi i 10 laghi di montagna in Trentino-Alto Adige in cui posso andare domani con il meteo migliore",
  "Dimmi i 10 paesi di montagna in Trentino-Alto Adige in cui posso andare domani con il meteo migliore",
];

type UiMessage = {
  id: string;
  role: "user" | "assistant";
  content?: string;
  answer?: TravelChatAnswer;
  pending?: boolean;
};

function historyFrom(messages: UiMessage[]): ChatMessage[] {
  return messages.filter((message) => !message.pending).map((message) => ({
    role: message.role,
    content: message.content ?? (message.answer ? `${message.answer.summary}\n${message.answer.recommendations.map((item) => item.name).join(", ")}` : ""),
  })).filter((message) => message.content);
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60), rest = minutes % 60;
  return hours ? `${hours} h${rest ? ` ${rest} min` : ""}` : `${rest} min`;
}

function fromStored(message: StoredChatMessage): UiMessage {
  return { id: message.id, role: message.role, content: message.content, answer: message.answer };
}

export function AIChatPanel({ region, home, onOpenSettings }: { region: string; home?: HomeLocation; onOpenSettings: () => void }) {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState("");
  const [historyBusy, setHistoryBusy] = useState(true);
  const busy = useMemo(() => messages.some((message) => message.pending), [messages]);

  useEffect(() => {
    let active = true;
    api.chatHistory().then(({ messages: saved }) => { if (active) setMessages(saved.map(fromStored)); })
      .catch(() => undefined).finally(() => { if (active) setHistoryBusy(false); });
    return () => { active = false; };
  }, []);

  async function ask(value: string) {
    const question = value.trim();
    if (!question || busy) return;
    const history = historyFrom(messages);
    const userMessage: UiMessage = { id: crypto.randomUUID(), role: "user", content: question };
    const pendingId = crypto.randomUUID();
    setMessages((current) => [...current, userMessage, { id: pendingId, role: "assistant", pending: true }]);
    setInput("");
    try {
      const { answer } = await api.chat(question, history, region);
      setMessages((current) => current.map((message) => message.id === pendingId ? { id: pendingId, role: "assistant", answer } : message));
    } catch (cause) {
      const content = cause instanceof Error ? cause.message : "Assistente non disponibile";
      setMessages((current) => current.map((message) => message.id === pendingId ? { id: pendingId, role: "assistant", content } : message));
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void ask(input);
  }

  return <div className="panel-content ai-panel">
    <div className="section-heading ai-heading">
      <span className="eyebrow"><Sparkles /> Collegato ad AI</span>
      <h1>Dove andiamo?</h1>
      <p>Chiedi un confronto: riceverai una classifica visuale basata su meteo, quota e viaggio in auto.</p>
    </div>

    <button className="ai-home-status" onClick={onOpenSettings}>
      <Home /><span>{home ? <><strong>Partenza impostata</strong><small>{home.label}</small></> : <><strong>Aggiungi l’indirizzo di partenza</strong><small>Impostazioni · necessario per distanza e tempo in auto</small></>}</span><Settings />
    </button>

    {historyBusy && <div className="chat-history-loader"><span className="spinner" />Carico la conversazione…</div>}
    {!historyBusy && !messages.length && <div className="ai-suggestions">
      {suggestions.map((suggestion) => <button key={suggestion} onClick={() => void ask(suggestion)}><Bot /><span>{suggestion}</span></button>)}
    </div>}
    <div className="ai-messages" aria-live="polite" aria-busy={busy}>
      {messages.map((message) => <article className={`ai-message is-${message.role} ${message.answer ? "has-plan" : ""}`} key={message.id}>
        <span className="ai-avatar">{message.role === "assistant" ? <Bot /> : <UserRound />}</span>
        {message.pending ? <div className="ai-thinking" role="status" aria-label="L’assistente sta preparando la risposta"><span className="ai-thinking-dots"><i /><i /><i /></span><span>Confronto meteo, quote e percorsi…</span></div>
          : message.answer ? <TravelAnswer answer={message.answer} />
            : <div>{message.content}</div>}
      </article>)}
    </div>
    <form className="ai-compose" onSubmit={submit}>
      <textarea value={input} onChange={(event) => setInput(event.target.value)} placeholder={`Chiedi dove andare in ${region}…`} rows={3} maxLength={1200} />
      <button disabled={busy || !input.trim()} aria-label="Invia domanda"><Send /></button>
    </form>
    <small className="ai-disclaimer">Distanze e tempi sono stime del percorso più veloce senza traffico in tempo reale. Verifica sempre viabilità e sentieri.</small>
  </div>;
}

function TravelAnswer({ answer }: { answer: TravelChatAnswer }) {
  return <div className="travel-answer">
    <p className="travel-summary">{answer.summary}</p>
    <div className="travel-recommendations">{answer.recommendations.map((item, index) => <section className="travel-card" key={item.id}>
      <div className={`travel-weather weather-${weatherKind(item.weatherCode)}`}><WeatherIcon kind={weatherKind(item.weatherCode)} /></div>
      <div className="travel-card-copy">
        <span className="travel-rank">{String(index + 1).padStart(2, "0")}</span><h3>{item.name}</h3>
        <p>{item.reason}</p>
        <div className="travel-tags">
          <span><Mountain />{item.elevation.toLocaleString("it-IT")} m</span>
          <span><Navigation />{item.condition}</span>
          <span>{item.temperatureMin}–{item.temperatureMax} °C</span>
          <span>{item.precipitation} mm · {item.precipitationProbability}%</span>
          {item.drivingDistanceKm !== undefined && <span><Car />{item.drivingDistanceKm} km</span>}
          {item.drivingDurationMinutes !== undefined && <span><Clock3 />{formatDuration(item.drivingDurationMinutes)}</span>}
        </div>
      </div>
      <strong className="travel-score">{item.score}<small>/100</small></strong>
    </section>)}</div>
    {!answer.homeConfigured && <p className="travel-home-hint"><Home />Salva il tuo indirizzo per vedere distanza e tempo in auto.</p>}
    <p className="travel-note">{answer.note}</p>
  </div>;
}
